/**
 * Admin Levels Page
 *
 * Page for managing CEFR levels (Director only).
 * Lets the director add new levels (e.g. A2+, B2) and edit their shared
 * resources without depending on a developer.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "../../components/ui";
import {
  useAdminLevels,
  useCreateLevel,
  useDeleteLevel,
  useUpdateLevel,
} from "../../hooks/useAdmin";
import type { AdminLevel, CreateLevelRequest } from "../../types/admin";

const emptyForm: CreateLevelRequest = {
  code: "",
  name: "",
  order: 1,
  description: "",
  whiteboardUrl: "",
  meetUrl: "",
  homeworkTitle: "Study Planner",
  homeworkDescription: "",
  homeworkFileUrl: "",
};

export function AdminLevelsPage() {
  const { data: levels, isLoading } = useAdminLevels();
  const createLevel = useCreateLevel();
  const updateLevel = useUpdateLevel();
  const deleteLevel = useDeleteLevel();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState<AdminLevel | null>(null);
  const [form, setForm] = useState<CreateLevelRequest>(emptyForm);
  const [toDelete, setToDelete] = useState<AdminLevel | null>(null);

  const openCreate = () => {
    const nextOrder = (levels?.reduce((m, l) => Math.max(m, l.order ?? 0), 0) ?? 0) + 1;
    setForm({ ...emptyForm, order: nextOrder });
    setIsEditing(false);
    setSelected(null);
    setIsModalOpen(true);
  };

  const openEdit = (level: AdminLevel) => {
    setForm({
      code: level.code,
      name: level.name,
      order: level.order ?? 1,
      description: level.description ?? "",
      whiteboardUrl: level.whiteboardUrl ?? "",
      meetUrl: level.meetUrl ?? "",
      homeworkTitle: level.homeworkTitle ?? "",
      homeworkDescription: level.homeworkDescription ?? "",
      homeworkFileUrl: level.homeworkFileUrl ?? "",
    });
    setSelected(level);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelected(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing && selected) {
      await updateLevel.mutateAsync({ id: selected.id, data: form });
    } else {
      await createLevel.mutateAsync(form);
    }
    closeModal();
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    await deleteLevel.mutateAsync(toDelete.id);
    setToDelete(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const sortedLevels = [...(levels ?? [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
                Manage Levels
              </h1>
              <p className="text-earth-700 text-sm mt-1">
                Create and edit levels (e.g. A2+, B2) and their shared resources
              </p>
            </div>
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <i className="ri-add-line"></i>
              New Level
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-earth-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-earth-50 border-b border-earth-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Order
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Code
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Name
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-earth-100">
                {sortedLevels.map((level) => (
                  <tr key={level.id} className="hover:bg-earth-50/50">
                    <td className="px-4 py-3 text-earth-600">{level.order}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-olive-100 text-olive-700 rounded text-xs font-semibold">
                        {level.code}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-earth-800 font-medium">
                      {level.name}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(level)}
                          className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <i className="ri-edit-line text-earth-500"></i>
                        </button>
                        <button
                          onClick={() => setToDelete(level)}
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-red-500"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sortedLevels.length === 0 && (
            <div className="text-center py-12 text-earth-700">
              No levels yet. Create the first one.
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={closeModal}></div>
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-earth-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-earth-800">
                {isEditing ? "Edit Level" : "New Level"}
              </h2>
              <button
                onClick={closeModal}
                className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl text-earth-500"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Code
                  </label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, code: e.target.value }))
                    }
                    placeholder="A2+"
                    maxLength={5}
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Order
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={form.order}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, order: Number(e.target.value) }))
                    }
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, name: e.target.value }))
                  }
                  placeholder="Pre-Intermediate"
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description: e.target.value }))
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500 resize-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Whiteboard URL
                  </label>
                  <input
                    type="url"
                    value={form.whiteboardUrl ?? ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, whiteboardUrl: e.target.value }))
                    }
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Google Meet URL
                  </label>
                  <input
                    type="url"
                    value={form.meetUrl ?? ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, meetUrl: e.target.value }))
                    }
                    placeholder="https://meet.google.com/..."
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Homework / Study Planner link
                </label>
                <input
                  type="url"
                  value={form.homeworkFileUrl ?? ""}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, homeworkFileUrl: e.target.value }))
                  }
                  placeholder="https://drive.google.com/..."
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  disabled={createLevel.isPending || updateLevel.isPending}
                  className="flex-1 px-4 py-2.5 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {createLevel.isPending || updateLevel.isPending
                    ? "Saving..."
                    : isEditing
                      ? "Save Changes"
                      : "Create Level"}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 text-earth-600 hover:text-earth-800 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {toDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setToDelete(null)}
          ></div>
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-xl text-red-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-earth-800 mb-2">
              Delete Level?
            </h3>
            <p className="text-sm text-earth-700 mb-6">
              <strong>
                {toDelete.code} - {toDelete.name}
              </strong>
              . This is only allowed if no lessons or class groups use it.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDelete}
                disabled={deleteLevel.isPending}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {deleteLevel.isPending ? "Deleting..." : "Delete"}
              </button>
              <button
                onClick={() => setToDelete(null)}
                className="flex-1 px-4 py-2.5 border border-earth-200 text-earth-600 hover:bg-earth-50 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminLevelsPage;
